import { type CollectionSummaryJSONObject, type FormSummaryJSONObject, isSystemTag } from '@ikegps/core-api';
import { CollectionModel, IkeModel } from '@ikegps/core-shared';
import type { IkeCollectionUpgradeFailure } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { Services } from '../../services';
import { Logger } from '../../services/log.service';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';

export interface CollectionStat {
	collectionId: string;
	collectionTitle: string;
	collectionRevision: string;
	formUuid: string;
	actualFormRevision: string;
	actualFormDate: string;
	latestFormRevision?: string;
	latestFormName?: string;
	latestFormDate?: string;
}

export interface FormUpdateFailure {
	failure: IkeCollectionUpgradeFailure;
	collectionId: string;
	collectionTitle: string;
}

export class CollectionFormModalCtrl {
	collections: CollectionSummaryJSONObject[];
	collectionStats: CollectionStat[] = [];
	failures: FormUpdateFailure[] = [];
	@observable warnings: FormUpdateFailure[] = [];

	@observable updatingForm: boolean = false;
	@computed get hasWarnings() {
		return Boolean(this.warnings.length);
	}

	constructor(collections: CollectionSummaryJSONObject[]) {
		makeObservable(this);

		this.collections = collections;
	}

	async updateFormCollections(forceUpdate: boolean = false) {
		this.setUpdatingForm(true);
		this.failures = [];
		this.warnings = [];

		const outputs: { collectionModel: CollectionModel; targetFormRevision: string }[] = [];
		for (const stat of this.collectionStats) {
			if (!stat.latestFormRevision) {
				return;
			}

			if (stat.actualFormRevision === stat.latestFormRevision) {
				continue;
			}

			const formModel = await AppStore.Form.fetch(stat.latestFormRevision);
			const collectionModel = await AppStore.Collection.fetch(stat.collectionRevision);

			if (!collectionModel) {
				continue;
			}

			const upgrade = IkeModel.Collection.upgrade([collectionModel], formModel);
			const output = await upgrade.process(Logger);

			if (output.result !== 'success') {
				output.fields.forEach((field) => {
					this.failures.push({
						failure: field,
						collectionId: stat.collectionId,
						collectionTitle: stat.collectionTitle,
					});
				});

				// When forcing, still queue the collection for save (save endpoint will strip invalid data)
				if (forceUpdate) {
					outputs.push({ collectionModel, targetFormRevision: stat.latestFormRevision });
				}
			} else {
				outputs.push({ collectionModel, targetFormRevision: stat.latestFormRevision });
			}
		}

		// If there are failures and not forcing, show warning state instead of blocking
		if (this.failures.length > 0 && !forceUpdate) {
			this.warnings = [...this.failures];
			this.setUpdatingForm(false);
			return; // Return early - modal will detect hasWarnings and show warning UI
		}

		// Save collections with swapped formRevision - save endpoint will strip invalid data
		const newCollectionUuids: string[] = [];
		for (const { collectionModel, targetFormRevision } of outputs) {
			const savedCollection = await this.saveCollectionWithFormRevision(collectionModel, targetFormRevision);
			newCollectionUuids.push(savedCollection.uuid);
		}

		await CurrentState.updateInspectedCollection();
		await this.refresh(newCollectionUuids);
		this.setUpdatingForm(false);
	}

	async saveCollectionWithFormRevision(collectionModel: CollectionModel, targetFormRevision: string) {
		// Fetch new and old model
		const sourceFormModel = await AppStore.Form.fetch(collectionModel.schema.version);
		const targetFormModel = await AppStore.Form.fetch(targetFormRevision);

		const allTargetFields = new Set(targetFormModel.getFieldsRecursive.map((field) => field.id));

		// Create a deep clone of the collection object to not mess with active mobx state
		const collectionJson = await IkeModel.Collection.write(collectionModel);
		const dupedCollectionModel = IkeModel.Collection.read(collectionJson, sourceFormModel, collectionModel.loader);

		dupedCollectionModel.forEachField((field) => {
			// If the target form doesn't contain the current field, then drop this fields contents
			if (!allTargetFields.has(field.schema.id)) {
				field.Mutator.clear();
			}
		});

		const targetCollectionJson = await IkeModel.Collection.write(dupedCollectionModel);

		// Swap the form version to target form - save endpoint will strip invalid data
		targetCollectionJson.formversion = targetFormRevision;
		targetCollectionJson.data.version = targetFormRevision;

		const rest = Services.Department.rest;
		const savedCollection = await rest.save(rest.Objects.Collection, targetCollectionJson, {
			formUpdated: {
				fromRevision: sourceFormModel.version,
				fromVersion: sourceFormModel.state.displayVersion,
				toVersion: targetFormModel.state.displayVersion,
			},
		});
		return savedCollection;
	}

	private async refresh(collectionUuids: string[]) {
		const collectionSummaries = await Promise.all(
			collectionUuids.map((uuid) => AppStore.CollectionSummary.getFetchableByUuid(uuid).refetch())
		);
		return Promise.all([
			AppStore.Tag.refresh(),
			...this.getTagIds(collectionSummaries).map((tagId) => AppStore.TagSummary.getFetchable(tagId).refetch()),
		]);
	}

	private getTagIds(collectionSummaries: CollectionSummaryJSONObject[]): string[] {
		const tags: { [id: string]: boolean } = {};

		for (const collectionSummary of collectionSummaries) {
			for (const tag of collectionSummary.tags) {
				if (!tags[tag] && !isSystemTag(tag)) {
					tags[tag] = true;
				}
			}
		}

		return Object.keys(tags);
	}

	@action private setUpdatingForm(updatingForm: boolean) {
		this.updatingForm = updatingForm;
	}

	getCollectionStats(departmentForms: FormSummaryJSONObject[]): CollectionStat[] {
		const collectionStats: CollectionStat[] = [];

		this.collections.forEach((collectionSummary) => {
			const formSummary = AppStore.FormSummary.get(collectionSummary.formRevision);
			const stat: CollectionStat = {
				collectionId: collectionSummary.collectionId,
				collectionTitle: collectionSummary.title,
				collectionRevision: collectionSummary.revision,
				formUuid: formSummary ? formSummary.uuid : '',
				actualFormRevision: collectionSummary.formRevision,
				actualFormDate: formSummary && formSummary.updatedAt ? formSummary.updatedAt : '',
			};
			collectionStats.push(stat);
		});

		collectionStats.forEach((stat) => {
			const form = stat.formUuid ? departmentForms.find(({ uuid }) => uuid === stat.formUuid) : null;
			if (form) {
				stat.latestFormRevision = form.revision;
				stat.latestFormName = form.displayname;
				stat.latestFormDate = form.updatedAt;
			}
		});

		this.collectionStats = collectionStats;

		return collectionStats;
	}
}
